import { ChangeDetectionStrategy, Component, computed, input } from "@angular/core";
import { MatCard } from "@angular/material/card";
import { MatChip } from "@angular/material/chips";
import { CardHeaderComponent } from "../../shared/card-header/card-header.component";
import { TypeChipComponent } from "../../shared/type-chip/type-chip.component";
import { FileBlockCardComponent } from "../file-block-card/file-block-card.component";
import { BlockDebugMetaComponent } from "../content-block-card/block-debug-meta/block-debug-meta.component";
import { mapLayerDisplayName } from "../../../../services/map-layer-block.service";
import { getMetadataValue } from "../../../../model/block.types";
import { resolveMapLayerContract } from "../../../../model/map-layer.types";
import type { BlockStatus, ContentBlockData } from "../../../../model/block.types";

/**
 * Dedicated card for the `map_layer` content kind (map-layer-block-ui S3, normalized onto the
 * standard card pattern by S11) — dispatched by the content dispatcher, never the generic
 * content-block card. The card is the geometry-card shell (content-block-card ref branch):
 * - the S8 layer name in the header (`map_layer.name` → filename minus extension → "AI Layer";
 *   `data.label` rides along as the raw/debug secondary name only),
 * - the embedded `FileBlockCardComponent` (embedded, kind `map_layer`) — it owns the whole
 *   data area (idle → Load → inline `ContentDataViewer`, error UX) AND the ONE standard
 *   `ai-card__footer` action bar with the registry geojson capabilities (zoom_to primary,
 *   add_layer, view_table, view_data, download, copy + the GEO_CONTENT_KINDS extras), so
 *   `view_data`/`view_table` work exactly like geometry once the data is loaded;
 * - the contextual add-layer label ("Add layer"/"Update layer", S2/S3) rides to that bar via
 *   the additive `addLayerLabel` input; ActionService routes the map_layer `add_layer` to
 *   MapLayerBlockService (chat-container scope, S11), which owns the update-vs-add decision,
 *   the fetch, the inline style bridge and the S10 toasts. ZERO custom action UI lives here.
 * - one chips row with the metadata summary (feature_count, bbox, content_summary) and the
 *   inline style preview (`style` type + `style_metadata.generated_by`) — no fetch needed.
 */
@Component({
  selector: "app-map-layer-card",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatCard,
    MatChip,
    CardHeaderComponent,
    TypeChipComponent,
    FileBlockCardComponent,
    BlockDebugMetaComponent,
  ],
  templateUrl: "./map-layer-card.component.html",
  styleUrl: "./map-layer-card.component.scss",
})
export class MapLayerCardComponent {
  data = input.required<ContentBlockData>();

  /** Dispatcher binding (content-dispatcher.component.html). The S11 card no longer gates
   * anything on it — the standard action bar lives in the embedded file-block-card — but the
   * input stays so the dispatcher contract is unchanged. */
  status = input.required<BlockStatus>();

  /** Sanctioned accessor — `map_layer` is never read raw (block.types.ts house style). */
  protected readonly contract = computed(() => resolveMapLayerContract(this.data()));

  /** S2 decision input: ONLY layer_id presence switches the contextual label. */
  protected readonly layerId = computed(() => this.contract()?.layer_id ?? null);

  /** S8 naming chain for the card header. */
  protected readonly displayName = computed(() =>
    mapLayerDisplayName(this.contract()?.name ?? null, this.data().ref?.filename),
  );

  protected readonly featureCount = computed(() => getMetadataValue(this.data().metadata, "feature_count"));

  protected readonly bboxPresent = computed(() => {
    const bbox = this.data().metadata?.["bbox"];
    return Array.isArray(bbox) && bbox.length > 0;
  });

  protected readonly contentSummary = computed(() => getMetadataValue(this.data().metadata, "content_summary"));

  /** Inline style preview: the flat/rules type when known, a neutral label for other shapes. */
  protected readonly styleLabel = computed((): string | null => {
    const style = this.contract()?.style;
    if (!style) return null;
    const type = style["type"];
    if (type === "flat" || type === "rules") return `STYLE ${String(type).toUpperCase()}`;
    return "STYLED";
  });

  /** Style provenance from the inline metadata (S3: preview without fetch). */
  protected readonly styleAuthorLabel = computed((): string | null => {
    const generatedBy = this.contract()?.style_metadata?.["generated_by"];
    return typeof generatedBy === "string" && generatedBy !== "" ? `STYLE BY ${generatedBy.toUpperCase()}` : null;
  });

  /** Contextual add-layer label (S2/S3), handed to the embedded bar via `addLayerLabel`. */
  protected readonly ctaLabel = computed(() => (this.layerId() !== null ? "Update layer" : "Add layer"));
}
